import React, { useEffect, useMemo, useState, useRef } from 'react';
import { X, Camera, Image as ImageIcon, LinkedinLogo, FacebookLogo, Trash, Plus } from '@phosphor-icons/react';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Spinner } from '../ui/Spinner';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PET_TYPES } from '../../lib/constants';
import { cn } from '../../lib/utils';
const ACCEPTED_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
export type EditProfileFocusSection = 'identity' | 'social' | 'about' | 'lifestyle';
export interface ProfileEditData {
  avatar: string;
  firstName: string;
  lastName: string;
  occupation: string;
  linkedinUrl: string | null;
  facebookUrl: string | null;
  bio: string;
  isSmoker: boolean;
  hasPets: boolean;
  petTypes: string[];
  petOther: string;
  livingMode: 'single' | 'couple' | 'family' | 'combo';
}
interface EditProfileModalProps {
  open: boolean;
  initialData: ProfileEditData;
  onClose: () => void;
  onSave: (data: ProfileEditData) => void;
  focusSection?: EditProfileFocusSection;
}
const LIVING_MODE_OPTIONS: Array<{
  value: ProfileEditData['livingMode'];
  label: string;
}> = [{
  value: 'single',
  label: 'Singur(ă)'
}, {
  value: 'couple',
  label: 'Cuplu'
}, {
  value: 'family',
  label: 'Familie'
}, {
  value: 'combo',
  label: 'Co-living'
}];
type ErrorMap = Partial<Record<keyof ProfileEditData, string>>;
export function EditProfileModal({
  open,
  initialData,
  onClose,
  onSave,
  focusSection
}: EditProfileModalProps) {
  const [data, setData] = useState<ProfileEditData>(initialData);
  const [errors, setErrors] = useState<ErrorMap>({});
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [photoError, setPhotoError] = useState<string | undefined>();
  const [showLinkedinInput, setShowLinkedinInput] = useState(!!initialData.linkedinUrl);
  const [showFacebookInput, setShowFacebookInput] = useState(!!initialData.facebookUrl);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const sectionRefs = {
    identity: useRef<HTMLDivElement>(null),
    social: useRef<HTMLDivElement>(null),
    about: useRef<HTMLDivElement>(null),
    lifestyle: useRef<HTMLDivElement>(null)
  };
  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setData(initialData);
      setErrors({});
      setShowLinkedinInput(!!initialData.linkedinUrl);
      setShowFacebookInput(!!initialData.facebookUrl);
    }
  }, [open, initialData]);
  // Scroll to focus section on open
  useEffect(() => {
    if (!open || !focusSection) return;
    const t = setTimeout(() => {
      const ref = sectionRefs[focusSection];
      ref?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      if (focusSection === 'social') {
        setShowLinkedinInput(true);
      }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusSection]);
  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  const hasChanges = useMemo(() => JSON.stringify(initialData) !== JSON.stringify(data), [initialData, data]);
  const update = <K extends keyof ProfileEditData,>(key: K, value: ProfileEditData[K]) => {
    setData(d => ({
      ...d,
      [key]: value
    }));
    setErrors(e => {
      if (!e[key]) return e;
      const next = {
        ...e
      };
      delete next[key];
      return next;
    });
  };
  const togglePetType = (val: string) => {
    setData(d => {
      const current = d.petTypes || [];
      return {
        ...d,
        petTypes: current.includes(val) ? current.filter(p => p !== val) : [...current, val]
      };
    });
  };
  const validate = (): boolean => {
    const next: ErrorMap = {};
    if (!data.firstName.trim()) next.firstName = 'Prenumele este obligatoriu';
    if (!data.lastName.trim()) next.lastName = 'Numele este obligatoriu';
    if (!data.occupation.trim()) next.occupation = 'Ocupația este obligatorie';
    if (data.linkedinUrl && !data.linkedinUrl.includes('linkedin.com')) {
      next.linkedinUrl = 'Link LinkedIn invalid';
    }
    if (data.facebookUrl && !data.facebookUrl.includes('facebook.com')) {
      next.facebookUrl = 'Link Facebook invalid';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) {
      // Scroll to first error
      const firstErrorKey = Object.keys(errors)[0] as keyof ProfileEditData | undefined;
      if (firstErrorKey === 'firstName' || firstErrorKey === 'lastName' || firstErrorKey === 'occupation') {
        sectionRefs.identity.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else if (firstErrorKey === 'linkedinUrl' || firstErrorKey === 'facebookUrl') {
        sectionRefs.social.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      return;
    }
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    onSave({
      ...data,
      linkedinUrl: data.linkedinUrl?.trim() || null,
      facebookUrl: data.facebookUrl?.trim() || null
    });
    setSaving(false);
  };
  const handleClose = () => {
    if (saving) return;
    if (hasChanges) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  };
  const handlePhotoFile = (file: File | null) => {
    if (!file) return;
    if (!ACCEPTED_IMG_TYPES.includes(file.type)) {
      setPhotoError('Format invalid. Folosește JPEG, PNG, WebP sau AVIF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPhotoError('Fișier prea mare. Maxim 10MB.');
      return;
    }
    setPhotoError(undefined);
    const reader = new FileReader();
    reader.onload = e => {
      const dataUrl = e.target?.result as string;
      update('avatar', dataUrl);
      setPhotoSheetOpen(false);
    };
    reader.onerror = () => setPhotoError('Eroare la citirea fișierului.');
    reader.readAsDataURL(file);
  };
  const handleRemovePhoto = () => {
    update('avatar', '');
    setPhotoError(undefined);
    setPhotoSheetOpen(false);
  };
  if (!open) return null;
  return <>
      <div role="dialog" aria-modal="true" aria-label="Editează profil" className="fixed inset-0 z-50 bg-dark-1 flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-1178">
        {/* Sticky header */}
        <header className="sticky top-0 z-10 bg-dark-1 border-b border-dark-3 px-4 py-3 flex items-center justify-between gap-3" data-id="element-1179">
          <button type="button" onClick={handleClose} disabled={saving} aria-label="Închide" className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-2 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer disabled:opacity-50" data-id="element-1180">
            <X size={20} weight="bold" data-id="element-1181" />
          </button>
          <h2 className="text-base font-semibold text-light-1 truncate" data-id="element-1182">
            Editează profil
          </h2>
          <button type="button" onClick={handleSave} disabled={!hasChanges || saving} className={cn('hidden sm:inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md text-sm font-semibold transition-colors', hasChanges && !saving ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-1183">
            {saving ? <>
                <Spinner size="sm" data-id="element-1184" />
                Se salvează
              </> : 'Salvează'}
          </button>
          {/* Spacer on mobile to keep title visually centered */}
          <span className="w-9 h-9 shrink-0 sm:hidden" aria-hidden="true" data-id="element-1185" />
        </header>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto" data-id="element-1186">
          <div className="max-w-2xl mx-auto px-4 py-6 pb-32 sm:pb-8 space-y-8" data-id="element-1187">
            {/* SECTION: Photo */}
            <section className="flex flex-col items-center text-center" data-id="element-1188">
              <button type="button" onClick={() => setPhotoSheetOpen(true)} className="relative group rounded-full focus:outline-none" aria-label="Schimbă fotografia" data-id="element-1189">
                {data.avatar ? <img src={data.avatar} alt="Fotografia ta" className="w-24 h-24 rounded-full object-cover border-4 border-dark-3 group-hover:border-dark-4 transition-colors" data-id="element-1190" /> : <div className="w-24 h-24 rounded-full bg-dark-3 flex items-center justify-center text-3xl font-medium text-light-2 border-4 border-dark-3 group-hover:border-dark-4 transition-colors" data-id="element-1191">
                    {data.firstName[0] || '?'}
                  </div>}
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-lime-1 flex items-center justify-center border-4 border-dark-1" data-id="element-1192">
                  <Camera size={14} weight="bold" className="text-dark-1" data-id="element-1193" />
                </div>
              </button>
              <button type="button" onClick={() => setPhotoSheetOpen(true)} className="mt-3 text-sm font-medium text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-1194">
                Schimbă fotografia
              </button>
              {photoError && <p className="mt-2 text-xs text-red-1" data-id="element-1195">{photoError}</p>}
            </section>

            {/* SECTION: Identity */}
            <section ref={sectionRefs.identity} className="scroll-mt-20 space-y-4" data-id="element-1196">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-light-3" data-id="element-1197">
                Identitate
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1198">
                <div className="space-y-1.5" data-id="element-1199">
                  <label className="text-xs font-medium text-light-2" data-id="element-1200">
                    Prenume
                  </label>
                  <Input value={data.firstName} onChange={e => update('firstName', e.target.value)} aria-invalid={!!errors.firstName} data-id="element-1201" />
                  {errors.firstName && <p className="text-xs text-red-1" data-id="element-1202">{errors.firstName}</p>}
                </div>
                <div className="space-y-1.5" data-id="element-1203">
                  <label className="text-xs font-medium text-light-2" data-id="element-1204">
                    Nume
                  </label>
                  <Input value={data.lastName} onChange={e => update('lastName', e.target.value)} aria-invalid={!!errors.lastName} data-id="element-1205" />
                  {errors.lastName && <p className="text-xs text-red-1" data-id="element-1206">{errors.lastName}</p>}
                </div>
              </div>
              <div className="space-y-1.5" data-id="element-1207">
                <label className="text-xs font-medium text-light-2" data-id="element-1208">
                  Ocupație
                </label>
                <Input value={data.occupation} onChange={e => update('occupation', e.target.value)} placeholder="ex: Product Designer" aria-invalid={!!errors.occupation} data-id="element-1209" />
                {errors.occupation && <p className="text-xs text-red-1" data-id="element-1210">{errors.occupation}</p>}
              </div>
            </section>

            {/* SECTION: Social */}
            <section ref={sectionRefs.social} className="scroll-mt-20 space-y-4" data-id="element-1211">
              <div data-id="element-1212">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-light-3" data-id="element-1213">
                  Conturi sociale
                </h3>
                <p className="text-xs text-light-3 mt-1.5" data-id="element-1214">
                  Adăugarea conturilor sociale arată proprietarilor că ești o
                  persoană reală.
                </p>
              </div>

              {/* LinkedIn */}
              <div className="space-y-2" data-id="element-1215">
                {!showLinkedinInput && !data.linkedinUrl ? <button type="button" onClick={() => setShowLinkedinInput(true)} className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-dark-2 border border-dark-3 hover:border-dark-4 transition-colors cursor-pointer" data-id="element-1216">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-1217">
                      <LinkedinLogo size={20} weight="fill" className="text-light-3 shrink-0" data-id="element-1218" />
                      <span className="text-sm font-medium text-light-1" data-id="element-1219">
                        LinkedIn
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-lime-1 shrink-0" data-id="element-1220">
                      <Plus size={14} weight="bold" data-id="element-1221" />
                      Adaugă
                    </span>
                  </button> : <div className="space-y-1.5" data-id="element-1222">
                    <label className="text-xs font-medium text-light-2 flex items-center gap-2" data-id="element-1223">
                      <LinkedinLogo size={14} weight="fill" className="text-light-2" data-id="element-1224" />
                      LinkedIn URL
                    </label>
                    <div className="flex gap-2" data-id="element-1225">
                      <Input value={data.linkedinUrl || ''} onChange={e => update('linkedinUrl', e.target.value)} placeholder="linkedin.com/in/numele-tau" aria-invalid={!!errors.linkedinUrl} className="flex-1" data-id="element-1226" />
                      <button type="button" onClick={() => {
                    update('linkedinUrl', null);
                    setShowLinkedinInput(false);
                  }} className="shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-md text-light-3 hover:text-red-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Elimină LinkedIn" data-id="element-1227">
                        <Trash size={16} weight="regular" data-id="element-1228" />
                      </button>
                    </div>
                    {errors.linkedinUrl && <p className="text-xs text-red-1" data-id="element-1229">{errors.linkedinUrl}</p>}
                  </div>}
              </div>

              {/* Facebook */}
              <div className="space-y-2" data-id="element-1230">
                {!showFacebookInput && !data.facebookUrl ? <button type="button" onClick={() => setShowFacebookInput(true)} className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-dark-2 border border-dark-3 hover:border-dark-4 transition-colors cursor-pointer" data-id="element-1231">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-1232">
                      <FacebookLogo size={20} weight="fill" className="text-light-3 shrink-0" data-id="element-1233" />
                      <span className="text-sm font-medium text-light-1" data-id="element-1234">
                        Facebook
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-lime-1 shrink-0" data-id="element-1235">
                      <Plus size={14} weight="bold" data-id="element-1236" />
                      Adaugă
                    </span>
                  </button> : <div className="space-y-1.5" data-id="element-1237">
                    <label className="text-xs font-medium text-light-2 flex items-center gap-2" data-id="element-1238">
                      <FacebookLogo size={14} weight="fill" className="text-light-2" data-id="element-1239" />
                      Facebook URL
                    </label>
                    <div className="flex gap-2" data-id="element-1240">
                      <Input value={data.facebookUrl || ''} onChange={e => update('facebookUrl', e.target.value)} placeholder="facebook.com/numele-tau" aria-invalid={!!errors.facebookUrl} className="flex-1" data-id="element-1241" />
                      <button type="button" onClick={() => {
                    update('facebookUrl', null);
                    setShowFacebookInput(false);
                  }} className="shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-md text-light-3 hover:text-red-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Elimină Facebook" data-id="element-1242">
                        <Trash size={16} weight="regular" data-id="element-1243" />
                      </button>
                    </div>
                    {errors.facebookUrl && <p className="text-xs text-red-1" data-id="element-1244">{errors.facebookUrl}</p>}
                  </div>}
              </div>
            </section>

            {/* SECTION: About */}
            <section ref={sectionRefs.about} className="scroll-mt-20 space-y-3" data-id="element-1245">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-light-3" data-id="element-1246">
                Despre mine
              </h3>
              <Textarea value={data.bio} onChange={e => update('bio', e.target.value)} placeholder="Câteva rânduri despre tine — proprietarii apreciază transparența." rows={5} data-id="element-1247" />
              <p className="text-xs text-light-4" data-id="element-1248">
                {data.bio.length}{' '}
                {data.bio.length === 1 ? 'caracter' : 'caractere'}
                {data.bio.length < 50 && <span className="text-light-3" data-id="element-1249">
                    {' '}
                    · min 50 pentru profil complet
                  </span>}
              </p>
            </section>

            {/* SECTION: Lifestyle */}
            <section ref={sectionRefs.lifestyle} className="scroll-mt-20 space-y-5" data-id="element-1250">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-light-3" data-id="element-1251">
                Stil de viață
              </h3>

              {/* Smoker toggle */}
              <div className="bg-dark-2 rounded-2xl border border-dark-3 px-5" data-id="element-1252">
                <div className="flex items-center justify-between py-4" data-id="element-1253">
                  <span className="text-sm font-medium text-light-1" data-id="element-1254">
                    Sunt fumător
                  </span>
                  <button type="button" role="switch" aria-checked={data.isSmoker} onClick={() => update('isSmoker', !data.isSmoker)} className={cn('relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors', data.isSmoker ? 'bg-lime-1' : 'bg-dark-3')} data-id="element-1255">
                    <span className={cn('pointer-events-none block h-6 w-6 rounded-full bg-dark-1 shadow-lg transition-transform', data.isSmoker ? 'translate-x-5' : 'translate-x-0.5')} data-id="element-1256" />
                  </button>
                </div>

                {/* Pets toggle */}
                <div className="flex items-center justify-between py-4 border-t border-dark-3" data-id="element-1257">
                  <span className="text-sm font-medium text-light-1" data-id="element-1258">
                    Am animale de companie
                  </span>
                  <button type="button" role="switch" aria-checked={data.hasPets} onClick={() => update('hasPets', !data.hasPets)} className={cn('relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors', data.hasPets ? 'bg-lime-1' : 'bg-dark-3')} data-id="element-1259">
                    <span className={cn('pointer-events-none block h-6 w-6 rounded-full bg-dark-1 shadow-lg transition-transform', data.hasPets ? 'translate-x-5' : 'translate-x-0.5')} data-id="element-1260" />
                  </button>
                </div>

                {data.hasPets && <div className="pb-5 space-y-3 animate-in slide-in-from-top-2 fade-in duration-300" data-id="element-1261">
                    <div className="flex flex-wrap gap-2" data-id="element-1262">
                      {PET_TYPES.map(pet => {
                    const isSelected = data.petTypes.includes(pet.value);
                    return <button key={pet.value} type="button" onClick={() => togglePetType(pet.value)} className={cn('px-4 py-2 rounded-full text-sm font-medium transition-colors border', isSelected ? 'bg-lime-4 border-lime-1 text-lime-1' : 'bg-dark-1 border-dark-3 text-light-3 hover:border-dark-4 hover:text-light-2')} data-id="element-1263">
                            {pet.label}
                          </button>;
                  })}
                    </div>
                    {data.petTypes.includes('other') && <Input value={data.petOther} onChange={e => update('petOther', e.target.value)} placeholder="Spune-ne ce animal..." className="bg-dark-1 border-dark-3" data-id="element-1264" />}
                  </div>}
              </div>

              {/* Living mode pills */}
              <div className="space-y-2" data-id="element-1265">
                <label className="text-xs font-medium text-light-2" data-id="element-1266">
                  Situație familială
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1267">
                  {LIVING_MODE_OPTIONS.map(opt => {
                  const isSelected = data.livingMode === opt.value;
                  return <button key={opt.value} type="button" onClick={() => update('livingMode', opt.value)} className={cn('px-4 py-2 rounded-full text-sm font-medium transition-colors border', isSelected ? 'bg-lime-4 border-lime-1 text-lime-1' : 'bg-dark-1 border-dark-3 text-light-3 hover:border-dark-4 hover:text-light-2')} data-id="element-1268">
                        {opt.label}
                      </button>;
                })}
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Mobile sticky save footer */}
        <footer className="sm:hidden sticky bottom-0 z-10 bg-dark-1 border-t border-dark-3 px-4 py-3" data-id="element-1269">
          <button type="button" onClick={handleSave} disabled={!hasChanges || saving} className={cn('w-full inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors', hasChanges && !saving ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-1270">
            {saving ? <>
                <Spinner size="sm" data-id="element-1271" />
                Se salvează...
              </> : 'Salvează modificările'}
          </button>
        </footer>
      </div>

      {/* Hidden inputs for camera + gallery */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="user" onChange={e => {
      handlePhotoFile(e.target.files?.[0] || null);
      e.target.value = '';
    }} className="hidden" aria-hidden="true" data-id="element-1272" />
      <input ref={galleryInputRef} type="file" accept={ACCEPTED_IMG_TYPES.join(',')} onChange={e => {
      handlePhotoFile(e.target.files?.[0] || null);
      e.target.value = '';
    }} className="hidden" aria-hidden="true" data-id="element-1273" />

      {/* Photo action sheet */}
      {photoSheetOpen && <div role="dialog" aria-modal="true" aria-label="Schimbă fotografia" className="fixed inset-0 z-[60] bg-dark-1/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150" onClick={() => setPhotoSheetOpen(false)} data-id="element-1274">
          <div className="w-full sm:max-w-sm bg-dark-2 rounded-t-2xl sm:rounded-2xl border-t sm:border border-dark-3 overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 sm:slide-in-from-bottom-0 duration-200" onClick={e => e.stopPropagation()} data-id="element-1275">
            <div className="flex items-center justify-between px-4 py-3 border-b border-dark-3" data-id="element-1276">
              <h3 className="text-sm font-semibold text-light-1" data-id="element-1277">
                Schimbă fotografia
              </h3>
              <button type="button" onClick={() => setPhotoSheetOpen(false)} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" aria-label="Închide" data-id="element-1278">
                <X size={16} weight="bold" data-id="element-1279" />
              </button>
            </div>

            <ul className="py-1" data-id="element-1280">
              <li data-id="element-1281">
                <button type="button" onClick={() => cameraInputRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-1282">
                  <span className="shrink-0 w-9 h-9 rounded-md bg-dark-3 flex items-center justify-center" data-id="element-1283">
                    <Camera size={18} weight="regular" className="text-light-2" data-id="element-1284" />
                  </span>
                  <span className="flex-1 min-w-0" data-id="element-1285">
                    <span className="block text-sm font-medium text-light-1" data-id="element-1286">
                      Fă o poză
                    </span>
                    <span className="block text-xs text-light-3" data-id="element-1287">
                      Folosește camera dispozitivului
                    </span>
                  </span>
                </button>
              </li>
              <li data-id="element-1288">
                <button type="button" onClick={() => galleryInputRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-1289">
                  <span className="shrink-0 w-9 h-9 rounded-md bg-dark-3 flex items-center justify-center" data-id="element-1290">
                    <ImageIcon size={18} weight="regular" className="text-light-2" data-id="element-1291" />
                  </span>
                  <span className="flex-1 min-w-0" data-id="element-1292">
                    <span className="block text-sm font-medium text-light-1" data-id="element-1293">
                      Încarcă din galerie
                    </span>
                    <span className="block text-xs text-light-3" data-id="element-1294">
                      JPEG, PNG, WebP · max 10MB
                    </span>
                  </span>
                </button>
              </li>
              {data.avatar && <li className="border-t border-dark-3" data-id="element-1295">
                  <button type="button" onClick={handleRemovePhoto} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-1296">
                    <span className="shrink-0 w-9 h-9 rounded-md bg-red-4 flex items-center justify-center" data-id="element-1297">
                      <Trash size={18} weight="regular" className="text-red-1" data-id="element-1298" />
                    </span>
                    <span className="flex-1 min-w-0" data-id="element-1299">
                      <span className="block text-sm font-medium text-red-1" data-id="element-1300">
                        Elimină fotografia
                      </span>
                      <span className="block text-xs text-light-3" data-id="element-1301">
                        Profilul va afișa inițiala numelui
                      </span>
                    </span>
                  </button>
                </li>}
            </ul>

            {/* Safe area / bottom padding on mobile */}
            <div className="h-2 sm:h-0" data-id="element-1302" />
          </div>
        </div>}

      {/* Discard confirmation */}
      <ConfirmDialog open={confirmDiscard} title="Renunți la modificări?" description="Modificările nu vor fi salvate. Această acțiune nu poate fi anulată." confirmLabel="Da, renunță" cancelLabel="Continuă editarea" variant="danger" onConfirm={() => {
      setConfirmDiscard(false);
      onClose();
    }} onCancel={() => setConfirmDiscard(false)} data-id="element-1303" />
    </>;
}